/* eslint-disable max-len */
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import AdminTable from '../../../../components/AdminTable';
import EmptyRecord from '../../../../components/EmptyRecord';
import useFilterMethods from '../../../../customHooks/useFilterMethods';
import useGetQueryParams from '../../../../customHooks/useGetQueryParams';
import useTableColumn from '../../../../customHooks/useTableColumn';
import useToast from '../../../../customHooks/useToast';
import { GET_RENT_PAYMENT_ADMIN_DASHBOARD } from '../../../../graphql/admin_queries';
import useCustomQuery from '../../../../network/useCustomQuery';
import generateCSV from '../../../../utils/generateCSV';
import generatePDF from '../../../../utils/generatePdf';
import { adminHasPermission, cleanUpRentPayments } from '../../../../utils/helpers';
import { IRentCollectionTableRecords } from './RentCollectionTableRecords.types';
import RolesAndPermissionGuard from '../../../../components/RolesAndPermissionGuard';
import PermissionDenied from '../../../PermissionDenied';
import DashboardHeader from '../../../../components/DashboardHeader';
import AdminLinkTabs from '../../../../components/AdminLinkTabs';
import { useGlobalState } from '../../../../contexts';
import CustomerTabs from '../../../AdminCustomers/CustomerTabs';
import { AdminRouteName } from '../../../CONSTANTS';
import RentCollectionCardDetails from '../RentCollectionCardDetails';

import EmptyMerchantSettlement from '../../../../svgs/EmptyMerchantSettlement';

const limit = 14;
const RentCollectionTableRecords = ({
  defaultUrl, toggleStorageId, tabStatus, hasNameAndImage, permissionName, optionalColumn,
  optionalColumnLabelFormat,
}:IRentCollectionTableRecords) => {
  const { profile } = useGlobalState();
  const adminPermissions = profile?.role?.permissions || [];
  const Toast = useToast();
  const [getRentPaymentAdminDashboard, rentPaymentsLoading, rentPayments] = useCustomQuery({
    query: GET_RENT_PAYMENT_ADMIN_DASHBOARD,
    onError: () => {},
    onData: () => {},
  });
  const { getQuery, getFilterQueries } = useGetQueryParams();
  const search = getQuery('search') || '';
  const page = Number(getQuery('page'));
  const { push } = useHistory();

  const allColumns = [
    ['image', 'email', 'hasBottomValue', 'name'],
    'amountPaid',
    'date',
    ...(optionalColumn || []),
    'rentReporting',
    'status',
    'equifaxSubmitted',
  ];

  const columnLabelFormat = {
    name: 'Customer',
    amountPaid: 'Amount Paid',
    date: 'Date Paid',
    ...(optionalColumnLabelFormat || {}),
    rentReporting: 'Rent Reporting',
    status: 'Status',
    equifaxSubmitted: 'Reported',
  };

  const { getColumns, applyChanges, selectedColumns } = useTableColumn(allColumns, toggleStorageId);

  const [rentPaymentsRec, setRentPaymentsRec] = useState(null);

  const setPage = (pageNum) => {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('page', pageNum);
    push({
      search: searchParams.toString(),
    });
  };
  if (!page) {
    push(`${defaultUrl}?page=1&search=${search}`);
  }

  const onSearchChange = (e) => {
    push(`${defaultUrl}?page=1&search=${encodeURIComponent(e.target.value)}`);
  };

  const rentPaymentData = rentPaymentsRec?.data?.rentPayments;

  const TabsRaw = [
    {
      id: 1,
      name: 'All Rent',
      link: AdminRouteName.rentCollectionRoute,
      permission: 'can-view-all-repayments',
      count: rentPaymentData?.count || 0,
    },
    {
      id: 2,
      name: 'Total Rent Settled',
      link: AdminRouteName.rentCollectionSettledRoute,
      permission: 'can-view-all-repayments',
      count: rentPaymentData?.count || 0,
    },
    {
      id: 3,
      name: 'Total Rent Awaiting Settlement',
      link: AdminRouteName.rentCollectionAwaitingSettlement,
      permission: 'can-view-due-repayments',
      count: rentPaymentData?.count || 0,
    },
    {
      id: 4,
      name: 'Total Rent Refunded',
      link: AdminRouteName.rentCollectionRefunded,
      permission: 'can-view-upcoming-repayments',
      count: rentPaymentData?.count || 0,
    },
    {
      id: 4,
      name: 'Rent Pending payment',
      link: AdminRouteName.rentCollectionPendingPayment,
      permission: 'can-view-pending-rent-payment',
      count: rentPaymentData?.count || 0,
    },
  ];

  const Tabs = TabsRaw?.map((tab) => ({
    ...tab,
    disabled: !adminHasPermission(adminPermissions, tab.permission),
  }));

  useEffect(() => {
    if (page) {
      const result = getFilterQueries();
      getRentPaymentAdminDashboard({
        variables: {
          offset: (page - 1) * limit,
          limit,
          search,
          ...(tabStatus ? { statusFilter: tabStatus } : {}),
          filters: {
            from: result?.from,
            to: result?.to,
            ...(tabStatus ? {} : { status: result?.status }),
            minAmount: result.min ? Number(result.min) : 0,
            maxAmount: result.max ? Number(result.max) : 0,
          },
        },
      });
    }
  }, [page, search]);

  const onFilterSubmit = (result) => {
    getRentPaymentAdminDashboard({
      variables: {
        offset: 0,
        limit,
        search,
        ...(tabStatus ? { statusFilter: tabStatus } : {}),
        filters: {
          from: result?.from,
          to: result?.to,
          status: result?.status,
          minAmount: result?.min ? Number(result?.min) : 0,
          maxAmount: result?.max ? Number(result?.max) : 0,
        },
      },
    });
  };

  const {
    filterObj,
  } = useFilterMethods({
    allowedFilters: tabStatus ? ['dateRange', 'amountRange'] : ['dateRange', 'status', 'amountRange'],
    defaultUrl,
    cb: onFilterSubmit,
    Toast,
    customFilterInformation: {
      dateRange: {
        label: 'Period',
      },
      status: {
        label: 'Status',
        options: [
          {
            label: 'Awaiting',
            value: 'UNSETTLED',
          },
          {
            label: 'Payment Pending',
            value: 'PENDING',
          },
          {
            label: 'Settled',
            value: 'SETTLED',
          },
          {
            label: 'Refunded',
            value: 'REFUNDED',
          },
        ],
      },
    },
  });

  useEffect(() => {
    if (rentPayments?.getRentPaymentAdminDashboard?.data) {
      setRentPaymentsRec(rentPayments?.getRentPaymentAdminDashboard);
    }
  }, [rentPayments]);

  const [getExportRecord] = useCustomQuery({
    query: GET_RENT_PAYMENT_ADMIN_DASHBOARD,
    onError: () => {},
    onData: () => {},
  });

  const handleExport = async (type: string) => {
    try {
      const result = getFilterQueries();
      const output = await getExportRecord({
        variables: {
          offset: 0,
          limit: 2000,
          search,
          statusFilter: tabStatus,
          filters: {
            from: result?.from,
            to: result?.to,
            status: result?.status,
            minAmount: result.min ? Number(result.min) : 0,
            maxAmount: result.max ? Number(result.max) : 0,
          },
        },
      });
      if (output?.data?.getRentPaymentAdminDashboard?.data?.rentPayments?.rows) {
        const rec = cleanUpRentPayments(output?.data?.getRentPaymentAdminDashboard?.data?.rentPayments?.rows);
        const func = type === 'pdf' ? generatePDF : generateCSV;
        const selectedHeaders = [
          'S/N',
          // change image__email__hasBottomValue__name concated columns to a single column of name
          ...(getColumns?.()?.map((column) => (column === 'image__email__hasBottomValue__name' ? ['name', 'email'] : column)) || []),
        ];

        func({
          payload: rec,
          title: 'Rewards',
          selectedHeaders,
          formatHeaders: columnLabelFormat,
          cb: () => Toast.success('Exported successfully'),
        });
      }
    } catch (error) {
      Toast.error('Error while exporting');
    }
  };

  const handleRoute = (data:any) => {
    push(`/rent-collection/rent-collection-details/${data?.id}`);
  };

  return (
    <div>

      <RentCollectionCardDetails payload={rentPaymentsRec?.data} />

      <DashboardHeader
        searchPlaceholder="Customer name / Phone number / Email address"
        search={search}
        onSearchChange={onSearchChange}
        searchLoading={rentPaymentsLoading}
        filterObj={filterObj}
        columnObject={{
          allColumns,
          formatLabels: columnLabelFormat,
          applyChanges,
          selectedColumns,
        }}
        disableSearch={!adminHasPermission(adminPermissions, 'can-search-customers-record')}
        disableExport={!adminHasPermission(adminPermissions, 'can-export-selected-data')}
        disbleFilter={!adminHasPermission(adminPermissions, 'can-filter-customer-record')}
        exportObj={{
          handleExport,
        }}
      >
        <AdminLinkTabs>
          <CustomerTabs Tabs={Tabs} />
        </AdminLinkTabs>

      </DashboardHeader>

      <RolesAndPermissionGuard permissionName={permissionName} hasErrorTemplate={<PermissionDenied height="60vh" />}>
        <div className="transaction-table-details-cover">
          {rentPaymentData?.count ? (
            <AdminTable
              refreshing={rentPaymentsLoading}
              page={page}
              setPage={setPage}
              pageSize={limit}
              minHeight="calc(100vh - 280px)"
              count={rentPaymentData?.count}
              contents={cleanUpRentPayments(rentPaymentData?.rows)}
              reference="id"
              hideDominantImage
              clickable
              handleClick={handleRoute}
              hasNameAndImage={hasNameAndImage}
              allowKeyArr={getColumns(true)}
              mobileAllowKeyArr={
                [
                  'status',
                  ['name', 'email'],
                  ['location', 'earnings'],
                ]
              }
              formatLabels={columnLabelFormat}
            />
          ) : (
            <EmptyRecord
              height="calc(100vh - 240px)"
              title={rentPaymentsLoading ? 'Fetching rewards...' : 'No Rent Payment yet'}
              main="All rent payments from customers will appear here"
              loading={rentPaymentsLoading}
              Icon={EmptyMerchantSettlement}
            />
          )}

        </div>
      </RolesAndPermissionGuard>

    </div>
  );
};

export default RentCollectionTableRecords;
